import { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../lib/translations';
import { calculateBasePrice } from '../../lib/pricingLogic';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import { FileText, Info, TableProperties, ArrowLeftRight } from 'lucide-react';

const PRICING_STEPS = [20, 40, 60, 80, 100, 120, 140, 160, 180, 200,
  240, 280, 320, 360, 400, 460, 520, 600, 700, 800, 900, 1000];

function PlanComparison({ documentCount, language }) {
  const ccy = t('common.currency', language);
  const forms = [
    { id: 'kpir', label: t('step1.kpir', language), color: '#1B3A7B' },
    { id: 'ryczalt', label: t('step1.ryczalt', language), color: '#E8941A' },
    { id: 'pelna', label: t('step1.pelna', language), color: '#16a34a' },
  ];
  const prices = forms.map(f => ({ ...f, price: calculateBasePrice(f.id, documentCount) }));
  const min = Math.min(...prices.map(p => p.price));

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4 text-center">
        {language === 'pl' ? `Porównanie dla ${documentCount} dokumentów` : `Comparison for ${documentCount} documents`}
      </p>
      <div className="grid grid-cols-3 gap-3">
        {prices.map(f => (
          <div key={f.id} className={`rounded-xl border-2 p-4 text-center transition-all ${f.price === min ? 'border-[#1B3A7B] bg-blue-50/50 shadow-md' : 'border-slate-200'}`}>
            {f.price === min && (
              <span className="inline-block text-[10px] font-semibold bg-[#1B3A7B] text-white px-2 py-0.5 rounded-full mb-2">
                {language === 'pl' ? 'Najkorzystniejsza' : 'Best value'}
              </span>
            )}
            <div className="text-xs font-medium text-slate-500 mb-1">{f.label}</div>
            <div className="font-mono-price text-2xl font-bold" style={{ color: f.color }}>{f.price}</div>
            <div className="text-xs text-slate-400">{ccy} / {language === 'pl' ? 'mies.' : 'mo.'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingTable({ language }) {
  const ccy = t('common.currency', language);
  const forms = ['kpir', 'ryczalt', 'pelna'];
  const labels = {
    kpir: t('step1.kpir', language),
    ryczalt: t('step1.ryczalt', language),
    pelna: t('step1.pelna', language),
  };

  return (
    <div className="overflow-auto max-h-[60vh]">
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 bg-[#1B3A7B] text-white">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-xs">
              {language === 'pl' ? 'Dokumenty' : 'Documents'}
            </th>
            {forms.map(f => (
              <th key={f} className="px-3 py-2 text-right font-medium text-xs">{labels[f]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PRICING_STEPS.map((docs, i) => (
            <tr key={docs} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
              <td className="px-3 py-1.5 font-mono-price font-medium text-slate-700 text-xs">
                {docs}
              </td>
              {forms.map(f => (
                <td key={f} className="px-3 py-1.5 text-right font-mono-price text-xs text-slate-600">
                  {calculateBasePrice(f, docs)} {ccy}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-400 text-center mt-3 px-3">
        {language === 'pl'
          ? 'Ceny pomiędzy podanymi progami są interpolowane liniowo. Wszystkie ceny netto.'
          : 'Prices between listed thresholds are linearly interpolated. All prices are net.'}
      </p>
    </div>
  );
}

export default function Step2Documents({ formData, updateField }) {
  const { language } = useLanguage();
  const [showInfo, setShowInfo] = useState(false);

  const basePrice = calculateBasePrice(formData.accountingForm, formData.documentCount);

  const handleInputChange = (e) => {
    const val = parseInt(e.target.value) || 0;
    updateField('documentCount', Math.max(20, Math.min(1000, val)));
  };

  return (
    <div data-testid="step-2-documents">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A7B] mb-2">
        {t('step2.title', language)}
      </h2>
      <p className="text-slate-500 mb-8 text-base">
        {t('step2.subtitle', language)}
      </p>

      <div className="max-w-lg mx-auto">
        {/* Main Input Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#1B3A7B] text-white flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{t('step2.docCount', language)}</h3>
              <p className="text-xs text-slate-500">{t('step2.docHint', language)}</p>
            </div>
          </div>

          {/* Number Display */}
          <div className="text-center mb-6">
            <Input
              type="number"
              value={formData.documentCount}
              onChange={handleInputChange}
              min={20}
              max={1000}
              step={20}
              className="text-center font-mono-price text-4xl font-bold h-16 w-32 mx-auto border-slate-300 focus:border-[#1B3A7B] focus:ring-[#1B3A7B]"
              data-testid="document-count-input"
            />
          </div>

          {/* Slider */}
          <div className="px-2">
            <Slider
              value={[formData.documentCount]}
              onValueChange={([val]) => updateField('documentCount', val)}
              min={20}
              max={1000}
              step={20}
              data-testid="document-count-slider"
              className="mb-2"
            />
            <div className="relative h-5">
              {[20, 200, 400, 600, 800, 1000].map(val => (
                <span
                  key={val}
                  className="absolute text-xs text-slate-400 font-mono-price -translate-x-1/2"
                  style={{ left: `${((val - 20) / 980) * 100}%` }}
                >
                  {val}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Price Preview */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm text-slate-500">{t('step2.totalEntries', language)}: </span>
              <span className="font-mono-price font-bold text-[#1B3A7B]">{formData.documentCount}</span>
            </div>
            <div className="text-right">
              <div className="font-mono-price text-xl font-bold text-[#E8941A]">
                {basePrice} {t('common.currency', language)}
              </div>
              <span className="text-xs text-slate-400">{t('common.net', language)}</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-500 text-center" data-testid="net-price-info">
              {t('step2.netPriceInfo', language)}
            </p>
          </div>
        </div>

        {/* Pricing Table + Comparison + Info */}
        <div className="mt-6 flex flex-col sm:flex-row items-start gap-4">
          <Dialog>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 text-sm text-[#1B3A7B] hover:underline font-medium" data-testid="view-pricing-table-btn">
                <TableProperties className="w-4 h-4" />
                {t('step2.viewPricing', language)}
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-[#1B3A7B]">{t('step2.pricingTableTitle', language)}</DialogTitle>
              </DialogHeader>
              <PricingTable language={language} />
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 text-sm text-[#E8941A] hover:underline font-medium" data-testid="compare-plans-btn">
                <ArrowLeftRight className="w-4 h-4" />
                {language === 'pl' ? 'Porównaj plany' : 'Compare plans'}
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-[#1B3A7B]">{language === 'pl' ? 'Porównanie planów' : 'Plan comparison'}</DialogTitle>
              </DialogHeader>
              <PlanComparison documentCount={formData.documentCount} language={language} />
            </DialogContent>
          </Dialog>

          <button onClick={() => setShowInfo(!showInfo)} className="flex items-center gap-2 text-sm text-[#1B3A7B] hover:underline" data-testid="doc-info-toggle">
            <Info className="w-4 h-4" />
            {t('step2.whatIsEntry', language)}
          </button>
        </div>

        {showInfo && (
          <div className="mt-3 bg-blue-50/50 border border-blue-100 rounded-lg p-4 text-sm text-slate-600 leading-relaxed animate-fade-in">
            {t('step2.entryExplanation', language)}
          </div>
        )}

        {/* Hint */}
        <p className="mt-4 text-sm text-slate-500 text-center">
          {t('step2.avgInfo', language)}
        </p>
      </div>
    </div>
  );
}
